import {Component, ElementRef, HostBinding, HostListener, Input, OnInit} from "@angular/core";
import {FormGroup} from "@angular/forms";
import {NodeModel} from "./model/NodeModel";
import {TreeBuilderUtils} from "./utils/tree-builder-utils";
import * as lunr from "lunr";
import {Index} from "lunr";
import {isNullOrUndefined} from "util";
import {ItemModel} from "../list/model/item.model";
import {BackofficeBusinessTypeCategoriesService} from "../../../../../../shared/api/service/backoffice-business-type-categories.service";
import {BusinessTypeCategory} from "../../../../../../../../common/api/dictionaries";

@Component({
    selector: 'tfl-filtered-tree-view',
    template: `
        <div class="filtered-container" [formGroup]="form">
            <tfl-filter-business-type
                    [placeholder]="placeholder"
                    [controlName]="filterControl"
                    [form]="form"
                    [searchTimeOut]="700"
                    (search)="onSearch($event)"
                    (click)="onClickInput()"
            ></tfl-filter-business-type>
            <div class="scroll" [ngStyle]="{'display': hideList ? 'none': 'block'}">
                <ng-container *ngIf="!result; else searchResult">
                    <tfl-tree-view-container [tree]="tree"
                    ></tfl-tree-view-container>
                </ng-container>
                <ng-template #searchResult>
                    <tfl-selectable-list [data]="result"
                                         [formControlName]="resultListControl"
                    ></tfl-selectable-list>
                </ng-template>
            </div>
        </div>
    `
})
export class FilteredTreeViewComponent implements OnInit {
    @Input('form') form: FormGroup;
    @Input('filterControl') filterControl: string;
    @Input('resultListControl') resultListControl: string;
    @Input('placeholder') placeholder: string;
    @Input('searchTimeOut') searchTimeOut: number;
    @Input('businessCategoriesList') businessCategoriesList: any;

    private tree: NodeModel[];
    private indx: Index;
    private hideList: boolean = true;
    private tout;
    private result: any[];
    private searchList: ItemModel[] = [];

    @HostBinding('class.border') private border: boolean = false;
    @HostBinding('attr.tabindex') tabindex = '0';

    @HostListener('document:click', ['$event']) click(event) {
        if (!this.elemRef.nativeElement.contains(event.target)) {
            this.hideListAndBorder();
            this.form.controls[this.filterControl].setValue(null);
            this.onSearch(undefined);
        }
    }

    constructor(private http: BackofficeBusinessTypeCategoriesService, private elemRef: ElementRef) {
    }


    onSearch(event) {
        if (event) {
            let searchResult = this.indx.query(query => {
                query.term(lunr.tokenizer(event), null);
            });
            this.result = this.filterAndSortResultListBySearchResult(searchResult);
            this.form.controls[this.resultListControl].setValue(this.result);
            if (this.result.length != 0) {
                this.showListAndBorder();
                return;
            }
            this.hideListAndBorder();
        } else {
            this.result = undefined;
            this.form.controls[this.resultListControl].setValue(this.result);
        }
    }

    onClickInput() {
        clearTimeout(this.tout);
        this.tout = setTimeout(() => {
            if (!isNullOrUndefined(this.result) && (this.result.length == 0)) {
                this.hideListAndBorder();
            } else {
                this.showListAndBorder();
            }
        }, 250)
    }

    ngOnInit(): void {
        this.tree = TreeBuilderUtils.generate(this.businessCategoriesList);
        this.tree.map(item => {
            TreeBuilderUtils.findAllLeavesOfNode(item, item.code + ' ' + item.name)
                .map(leave => {
                    this.searchList.push({
                        id: leave.node.id,
                        name: leave.node.name,
                        code: leave.node.code,
                        path: leave.path,
                        score: undefined,
                        metadata: undefined
                    });
                });
        });
        this.buildSearchIndex();
    }

    private buildSearchIndex() {
        this.indx = lunr(ctx => {
            lunr.tokenizer.separator = /[\s\-\\.;]+/;
            ctx.field('path', {boost: 2});
            ctx.field('name', {boost: 3});
            ctx.field('code', {boost: 1});
            ctx.ref('id');
            ctx.use(this.trigramizeToken);
            ctx.metadataWhitelist = ['position'];
            this.searchList.forEach(item => ctx.add(item));
        });
    }

    private hideListAndBorder() {
        this.hideList = true;
        this.border = false;
    }

    private showListAndBorder() {
        this.hideList = false;
        this.border = true;
        this.form.controls[this.filterControl].setErrors(null);
    }

    private filterAndSortResultListBySearchResult(searchResult): any[] {
        return this.searchList.filter(item =>
            searchResult
                .map(res => res.ref)
                .some(index => index == item.id)
        ).map(item => {
            searchResult.forEach(res => {
                if (res.ref == item.id) {
                    item.score = res.score;
                    item.metadata = res.matchData.metadata;
                }
            });
            return item;
        }).sort((a, b) => {
            return b.score - a.score;
        });
    }

    private trigramizeToken(builder) {
        const TRIGRAM_LENGTH = 3;
        let pipelineFunction = function (token) {
            let tokens: lunr.Token[] = [token];
            let str: string = token.toString();
            let offset: number = token.metadata.position[0];
            if (str.length > TRIGRAM_LENGTH) {
                for (let i = 0; i <= str.length - TRIGRAM_LENGTH; i++) {
                    let trigram = str.slice(i, i + TRIGRAM_LENGTH);
                    let metadata = {position: [offset + i, TRIGRAM_LENGTH]};
                    tokens.push(new lunr.Token(trigram, metadata));
                }
            }
            return tokens;
        };

        let searchPipelineFunction = function (token) {
            return trigram(token.toString()).map(str => {
                if (str == token.toString()) {
                    return new lunr.Token(str, token.metadata);
                }
                return new lunr.Token(str, null);
            });
        };

        function trigram(str: string): string[] {
            let trigrams: string[] = [];
            trigrams.push(str);
            if (str.length <= TRIGRAM_LENGTH) {
                return trigrams;
            }
            for (let i = 0; i < str.length; i++) {
                if (TRIGRAM_LENGTH + i > str.length) {
                    return trigrams;
                }
                trigrams.push(str.slice(i, TRIGRAM_LENGTH + i));
            }
            return trigrams;
        }

        lunr.Pipeline.registerFunction(pipelineFunction, 'trigramizeTokens');
        lunr.Pipeline.registerFunction(searchPipelineFunction, 'trigramizeSearchTokens');
        builder.pipeline.add(pipelineFunction);
        builder.searchPipeline.add(searchPipelineFunction);
    };
}


// WEBPACK FOOTER //
// ./~/angular2-template-loader!./~/angular-router-loader/src!./src/dashboard/app/modules/customers/shared/components/business-categories/tree/filtered-tree-view.component.ts